import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Header from "@/components/Header";
import SEO from "@/components/SEO";
import Footer from "@/components/Footer";
import { Database, Newspaper, CloudSun, HardDrives, ArrowsClockwise, CheckCircle, Warning, XCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dotColor = (ok) => (ok ? "bg-emerald-500" : "bg-red-500");

const timeAgo = (mins) => {
  if (mins === null || mins === undefined) return "unknown";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  return h < 24 ? `${h}h ${mins % 60}m ago` : `${Math.floor(h / 24)}d ago`;
};

const StatusCard = ({ icon: Icon, title, ok, lines, testId }) => (
  <div data-testid={testId} className="border border-[#E0D4C8] bg-white p-4 flex flex-col gap-2">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Icon size={20} className="text-[#0D7680]" />
        <h3 className="font-serif text-base text-[#33302E]">{title}</h3>
      </div>
      <span className={`w-3 h-3 rounded-full ${dotColor(ok)}`} title={ok ? "Healthy" : "Attention needed"}></span>
    </div>
    <div className="text-sm text-[#6B6661] space-y-1">
      {lines.map((l, i) => (
        <div key={l.label} className="flex justify-between gap-4">
          <span>{l.label}</span>
          <span className="text-[#33302E] font-medium text-right">{l.value}</span>
        </div>
      ))}
    </div>
  </div>
);

export default function StatusPage() {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/status`);
      setStatus(res.data);
      setError(false);
      setLastChecked(new Date());
    } catch (err) {
      console.error("Status fetch failed:", err);
      setError(true);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 60000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const overall = error ? "unreachable" : status?.status;
  const banner = {
    operational: { bg: "bg-emerald-600", Icon: CheckCircle, text: "All Systems Operational" },
    degraded: { bg: "bg-amber-500", Icon: Warning, text: "Partially Degraded - news scraper behind schedule" },
    down: { bg: "bg-red-600", Icon: XCircle, text: "Major Issue - database unreachable" },
    unreachable: { bg: "bg-red-600", Icon: XCircle, text: "Backend Unreachable" },
  }[overall] || { bg: "bg-[#6B6661]", Icon: ArrowsClockwise, text: "Checking..." };

  return (
    <div className="min-h-screen bg-[#FFF1E5]">
      <SEO title="System Status" description="Live health status of Farmwatch services - news scraper, database, weather feeds and API uptime." url="/status" />
      <Header />
      <main className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-serif text-3xl text-[#33302E] mb-1" data-testid="status-page-title">Farmwatch System Status</h1>
        <p className="text-sm text-[#6B6661] mb-6">
          Auto-refreshes every minute{lastChecked ? ` · Last checked ${lastChecked.toLocaleTimeString()}` : ""}
        </p>

        <div data-testid="status-overall-banner" className={`${banner.bg} text-white px-4 py-3 flex items-center gap-3 mb-6`}>
          <banner.Icon size={22} weight="fill" />
          <span className="font-medium">{banner.text}</span>
        </div>

        {status && !error && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <StatusCard
              icon={Newspaper}
              title="News Scraper"
              ok={status.scraper.healthy}
              testId="status-card-scraper"
              lines={[
                { label: "Last run", value: timeAgo(status.scraper.minutes_since_last_run) },
                { label: "Articles added (24h)", value: status.scraper.articles_added_24h },
                { label: "Schedule", value: status.scraper.schedule },
              ]}
            />
            <StatusCard
              icon={Database}
              title="Database"
              ok={status.database.connected}
              testId="status-card-database"
              lines={[
                { label: "Connection", value: status.database.connected ? "Connected" : "Down" },
                { label: "Total articles", value: status.database.total_articles.toLocaleString() },
              ]}
            />
            <StatusCard
              icon={CloudSun}
              title="Weather Service"
              ok={true}
              testId="status-card-weather"
              lines={[
                { label: "Provider", value: "Open-Meteo (live)" },
                { label: "Cache", value: status.weather_service.active ? "Warm" : "Cold (loads on first visit)" },
              ]}
            />
            <StatusCard
              icon={HardDrives}
              title="Server"
              ok={true}
              testId="status-card-server"
              lines={[
                { label: "Uptime", value: timeAgo(status.server.uptime_minutes).replace(" ago", "") },
                { label: "Checked at", value: new Date(status.checked_at).toLocaleTimeString() },
              ]}
            />
          </div>
        )}

        <button
          data-testid="status-refresh-button"
          onClick={fetchStatus}
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-[#0D7680] text-white text-sm hover:bg-[#0A5D65] transition-colors"
        >
          <ArrowsClockwise size={16} /> Refresh now
        </button>
      </main>
      <Footer />
    </div>
  );
}
